import React, { useState, useEffect } from "react";
import {
    Input,
    Tag,
    message,
    Row,
    Col,
    Typography,
    DatePicker,
    Select,
} from "antd";
import {
    SearchOutlined,
    InfoCircleOutlined,
    SendOutlined,
    ReloadOutlined,
    ClockCircleOutlined,
    UserOutlined,
    WalletOutlined,
    FileTextOutlined,
} from "@ant-design/icons";
import {
    collectionAPIWithErrorHandling,
    collectionHelpers,
} from "../../../api/collection/collection";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { mapZnsAPIWithErrorHandling } from "../../../api/zns/zns";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { actionColumnDef } from "../../../components/common/RowActions";
import { confirmAction } from "../../../components/common/confirmDialog";
import StatCard from "../../../components/common/StatCard";
import { feePaymentExtendedUntil } from "./CollectionDeadline";
import ListToolbar, {
    selectVisibleColumns,
    useListToolbar,
} from "../../../components/common/ListToolbar";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = [
    "name",
    "period",
    "overdue_days",
    "total",
    "amount_paid",
    "total_amount",
    "status",
];

export default function CusFeeTable() {
    const navigate = useNavigate();
    const [customerFees, setCusFees] = useState([]);
    const [loading, setLoading] = useState(false);

    const [sendOneLoading, setSendOneLoading] = useState(null);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        from: 0,
        to: 0,
        lastPage: 1,
    });
    const [searchText, setSearchText] = useState("");
    const [filters, setFilters] = useState({
        status: "",
        sort_by: "due_date",
        sort_order: "desc",
        overdue_range: "",
        start_date: "",
        end_date: "",
    });

    const [dashboard, setDashboard] = useState({
        total_overdue_amount: 0,
        total_customers: 0,
        total_collections: 0,
        avg_per_customer: 0,
    });

    const [dateRange, setDateRange] = useState([null, null]);

    const {
        visibleFilters, setVisibleFilters,
        visibleColumns, setVisibleColumns,
    } = useListToolbar({
        defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
        defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
    });

    // Fetch customerFee data using the proper API
    const fetchFees = async (
        page = 1,
        perPage = 10,
        search = "",
        status = "",
        overdue_range = filters.overdue_range,
        customDateRange = dateRange
    ) => {
        setLoading(true);
        try {
            const params = {
                page,
                per_page: perPage,
                search: search.trim(),
                status,
                sort_by: filters.sort_by,
                sort_order: filters.sort_order,
                overdue_range,
                start_date: filters.start_date,
                end_date: filters.end_date,
            };

            // Thêm date range vào params nếu có
            if (customDateRange[0] && customDateRange[1]) {
                params.start_date = customDateRange[0].format("YYYY-MM-DD");
                params.end_date = customDateRange[1].format("YYYY-MM-DD");
            }

            const response = await collectionAPIWithErrorHandling.getCusFees(params);
            const { fees: cusFeeData, pagination: paginationData } =
                collectionHelpers.extractCusFeeList(response);

            setCusFees(cusFeeData);
            setPagination({
                current: paginationData.current,
                pageSize: paginationData.pageSize,
                total: paginationData.total,
                from: paginationData.from,
                to: paginationData.to,
                lastPage: paginationData.lastPage,
            });
        } catch (error) {
            message.error(error.message || "Không thể tải danh sách đợt thu");
            console.error("Error fetching collections:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDashboard = async (
        page = 1,
        perPage = 10,
        search = "",
        status = "",
        overdue_range = filters.overdue_range,
        customDateRange = dateRange
    ) => {
        setLoading(true);
        try {
            const params = {
                page,
                per_page: perPage,
                search: search.trim(),
                status,
                sort_by: filters.sort_by,
                sort_order: filters.sort_order,
                overdue_range,
                start_date: filters.start_date,
                end_date: filters.end_date,
            };

            // Thêm date range vào params nếu có
            if (customDateRange[0] && customDateRange[1]) {
                params.start_date = customDateRange[0].format("YYYY-MM-DD");
                params.end_date = customDateRange[1].format("YYYY-MM-DD");
            }

            const response = await collectionAPIWithErrorHandling.getOverdueDashboard(params);
            setDashboard(response?.data?.data);
        } catch (error) {
            message.error(error.message || "Không thể tải thống kê đợt thu");
            console.error("Error fetching collections:", error);
        } finally {
            setLoading(false);
        }
    };

    // Load data on component mount
    useEffect(() => {
        fetchFees();
        fetchDashboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle table pagination change
    const handleTableChange = (paginationConfig, tableFilters, sorter) => {
        const newFilters = { ...filters };
        // Handle sorting
        if (sorter && sorter.field) {
            const sortBy = sorter.field;
            const sortOrder = sorter.order === "descend" ? "desc" : "asc";
            setFilters((prev) => ({
                ...prev,
                sort_by: sortBy,
                sort_order: sortOrder,
            }));
        }

        // Handle status filter
        let statusFilter = "";
        if (tableFilters && tableFilters.status && tableFilters.status.length > 0) {
            statusFilter = tableFilters.status[0];
        }

        fetchFees(
            paginationConfig.current,
            paginationConfig.pageSize,
            searchText,
            statusFilter,
            newFilters.overdue_range,
            dateRange
        );
    };

    // Handle search
    const handleSearch = (value) => {
        setSearchText(value);
        fetchFees(1, pagination.pageSize, value, filters.status, filters.overdue_range, dateRange);
        fetchDashboard(1, pagination.pageSize, value, filters.status, filters.overdue_range, dateRange);
    };

    // Handle clear search
    const handleClearSearch = () => {
        setSearchText("");
        fetchFees(1, pagination.pageSize, "", filters.status, filters.overdue_range, dateRange);
        fetchDashboard(1, pagination.pageSize, "", filters.status, filters.overdue_range, dateRange);
    };

    // Handle date range change
    const handleDateRangeChange = (dates) => {
        const newRange = dates || [null, null];
        setDateRange(newRange);
        fetchFees(1, pagination.pageSize, searchText, filters.status, filters.overdue_range, newRange);
        fetchDashboard(1, pagination.pageSize, searchText, filters.status, filters.overdue_range, newRange);
    };

    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);

        fetchFees(1, pagination.pageSize, searchText, newFilters.status, newFilters.overdue_range, dateRange);
        fetchDashboard(1, pagination.pageSize, searchText, newFilters.status, newFilters.overdue_range, dateRange);
    };

    // Xoá toàn bộ bộ lọc
    const handleClearFilters = () => {
        setSearchText("");
        setDateRange([null, null]);
        setFilters((prev) => ({ ...prev, overdue_range: "" }));
        fetchFees(1, pagination.pageSize, "", "", "", [null, null]);
        fetchDashboard(1, pagination.pageSize, "", "", "", [null, null]);
    };

    /*
     * Gửi tin cho ĐÚNG khoản thu của dòng này (`record.id`).
     *
     * Bản cũ gọi sendOneZns(record.collection_id, record.customer_id) theo chữ ký cũ,
     * trong khi API giờ là `send-one/{feeId}` và chỉ nhận một tham số — backend lấy
     * MÃ ĐỢT THU làm mã khoản thu, tức kiểm và gửi cho một khoản của người khác.
     *
     * Màn quá hạn có cả khoản chưa nhận tin lẫn đã nhận: chưa nhận thì gửi luôn, đã
     * nhận thì hỏi rồi gửi lại (gửi lần đầu sẽ bị backend chặn "đã gửi"). Cùng điều
     * kiện "đã gửi" với backend (referentid), giống nút từng dòng ở chi tiết đợt thu.
     */
    const sendOne = async (record) => {
        const isResend = !!record.referentid;

        if (isResend) {
            const sentAt = record.last_sent_at
                ? ` lúc ${dayjs(record.last_sent_at).format("DD/MM/YYYY HH:mm")}`
                : "";
            const confirmed = await confirmAction({
                title: "Gửi lại tin cho khách hàng này?",
                content: `Khách hàng đã nhận tin${sentAt}. Hệ thống sẽ gửi thêm một tin nữa.`,
                okText: "Gửi lại",
            });
            if (!confirmed) return;
        }

        try {
            setSendOneLoading(record.id);
            const response = isResend
                ? await mapZnsAPIWithErrorHandling.resendOneZns(record.id)
                : await mapZnsAPIWithErrorHandling.sendOneZns(record.id);

            message.success(
                response?.data?.message ?? "Hệ thống ghi nhận. Đang chờ xử lý!"
            );
        } catch (error) {
            message.error(error.message);
        } finally {
            setSendOneLoading(null);
        }
    };

    const handleCusInfo = (record) => {
        message.info(`Chi tiết thông tin khách hàng: ${record.customer?.name}`);
        navigate(`/customers/information/${record.customer_id}`);
    };

    const handleInfo = (record) => {
        message.info(`Chi tiết thông tin đợt thu: ${record.collection.name}`);
        navigate(`/collections/information/${record.collection.id}`);
    };

    // ===== Định nghĩa cột =====
    const columnDefs = [
        {
            key: "index", label: "STT", fixed: true,
            column: {
                title: "STT", key: "index", width: 60, fixed: "left",
                render: (_, __, index) =>
                    (pagination.current - 1) * pagination.pageSize + index + 1,
            },
        },
        {
            key: "customers", label: "Khách hàng", fixed: true,
            column: {
                title: "Khách hàng", key: "customers", width: "13%", fixed: "left",
                render: (_, record) => (
                    <div
                        onClick={() => handleCusInfo(record)}
                        style={{ cursor: "pointer", color: "#0070D7" }}
                    >
                        <div className="font-medium">
                            {record.customer?.name || `Khách hàng ${record.customer_id}`}
                        </div>
                        {record.customer?.phone && (
                            <Text type="secondary" className="text-xs">
                                {[record.customer.represent_name, record.customer.phone]
                                    .filter(Boolean)
                                    .join(" - ")}
                            </Text>
                        )}
                    </div>
                ),
            },
        },
        {
            key: "name", label: "Tên đợt thu",
            column: {
                title: "Tên đợt thu", dataIndex: "name", key: "name", width: 250,
                render: (_, record) => <div>{record.collection.name}</div>,
            },
        },
        {
            key: "period", label: "Hạn nộp",
            column: {
                title: "Hạn nộp", key: "period", align: "center", width: 150,
                render: (_, record) => {
                    const extended = feePaymentExtendedUntil(record);
                    return (
                        <div className="text-sm">
                            <Tag color="blue">{dayjs(record.due_date).format("DD/MM/YYYY")}</Tag>
                            {extended && (
                                <div className="mt-1 text-xs text-info-text">
                                    Gia hạn đến {extended.format("DD/MM/YYYY")}
                                </div>
                            )}
                        </div>
                    );
                },
            },
        },
        {
            key: "overdue_days", label: "Quá hạn",
            column: {
                title: "Quá hạn", key: "overdue_days", width: 140, align: "center",
                render: (_, record) => {
                    const diff = record.overdue_days;

                    if (["PAID", "CANCELLED"].includes(record.status)) {
                        return <Tag color="green">Không</Tag>;
                    }

                    if (diff <= 0) {
                        return <Tag color="blue">Chưa đến hạn</Tag>;
                    }

                    return <Tag color="red">{diff} ngày</Tag>;
                },
            },
        },
        {
            key: "total", label: "Tổng cộng",
            column: {
                title: "Tổng cộng", key: "total", width: 220, align: "right",
                render: (_, record) => {
                    const total = Number(record.amount_due || 0);
                    return (
                        <Text strong className="text-blue-600">
                            {total.toLocaleString("vi-VN")} ₫
                        </Text>
                    );
                },
            },
        },
        {
            key: "amount_paid", label: "Đã thanh toán",
            column: {
                title: "Đã thanh toán", key: "amount_paid", width: 220, align: "right",
                render: (_, record) => {
                    const total = Number(record.amount_paid || 0);
                    return <Text>{total.toLocaleString("vi-VN")} ₫</Text>;
                },
            },
        },
        {
            key: "total_amount", label: "Khoản nợ",
            column: {
                title: "Khoản nợ", key: "total_amount", width: 200, align: "right",
                render: (_, record) => {
                    const totalDue = Number(record.amount_due || 0);
                    const totalPaid = Number(record.amount_paid || 0);
                    const total = totalDue - totalPaid;
                    return <Tag color="red">{total.toLocaleString("vi-VN")} ₫</Tag>;
                },
            },
        },
        {
            key: "status", label: "Trạng thái",
            column: {
                title: "Trạng thái", key: "status", width: 220, align: "center",
                render: (_, record) => {
                    const statusLabels = {
                        PAID: "Đã thanh toán",
                        CANCELLED: "Đã hủy",
                        UNPAID: "Chưa thanh toán",
                        PARTIAL: "Một phần",
                    };

                    const statusColors = {
                        PAID: "green",
                        CANCELLED: "red",
                        UNPAID: "orange",
                        PARTIAL: "blue",
                    };

                    return (
                        <Tag color={statusColors[record.status] || "default"}>
                            {statusLabels[record.status] || record.status}
                        </Tag>
                    );
                },
            },
        },
        actionColumnDef(
            (record) => [
                { key: "info", label: "Xem chi tiết", icon: <InfoCircleOutlined />, tone: "view", onClick: () => handleInfo(record) },
                {
                    key: "send",
                    label: record.referentid
                        ? `Đã gửi tin${record.last_sent_at ? ` lúc ${dayjs(record.last_sent_at).format("DD/MM/YYYY HH:mm")}` : ""} — bấm để gửi lại`
                        : "Gửi tin nhắc thanh toán",
                    icon: record.referentid ? <ReloadOutlined /> : <SendOutlined />,
                    tone: "edit",
                    onClick: () => sendOne(record),
                    loading: sendOneLoading === record.id,
                    disabled: ["PAID", "CANCELLED"].includes(record.status),
                },
            ],
            { width: 100 }
        ),
    ];

    // ===== Định nghĩa bộ lọc =====
    const filterDefs = [
        {
            key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
            field: (
                <Input.Search
                    placeholder="Tìm kiếm theo tên đợt thu, mã đợt thu..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onSearch={handleSearch}
                    onClear={handleClearSearch}
                    enterButton={<SearchOutlined />}
                    allowClear
                    style={{ width: "100%" }}
                />
            ),
        },
        {
            key: "overdue_range", label: "Thời gian quá hạn", span: { xs: 24, sm: 12, md: 8 },
            field: (
                <Select
                    placeholder="Lọc theo thời gian quá hạn"
                    allowClear
                    optionLabelProp="label"
                    style={{ width: "100%" }}
                    value={filters.overdue_range || undefined}
                    onChange={(value) => handleFilterChange("overdue_range", value)}
                >
                    <Select.Option value="1_7" label="1-7 ngày">🟡 1-7 ngày</Select.Option>
                    <Select.Option value="8_30" label="8-30 ngày">🟠 8-30 ngày</Select.Option>
                    <Select.Option value="31_60" label="31-60 ngày">🔴 31-60 ngày</Select.Option>
                    <Select.Option value="60_plus" label="Trên 60 ngày">⚫ Trên 60 ngày</Select.Option>
                </Select>
            ),
        },
        {
            key: "dateRange", label: "Khoảng ngày", span: { xs: 24, sm: 12, md: 8 },
            field: (
                <RangePicker
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    placeholder={["Từ ngày", "Đến ngày"]}
                    value={dateRange}
                    onChange={handleDateRangeChange}
                />
            ),
        },
    ];

    const columns = selectVisibleColumns(columnDefs, visibleColumns);
    const hasActiveFilter = !!(
        searchText ||
        filters.overdue_range ||
        (dateRange[0] && dateRange[1])
    );

    return (
        <BrandedPageLayout
            title="Khoản thu khách hàng"
            subtitle="Theo dõi các khoản thu và tình trạng quá hạn của khách hàng."
            breadcrumbItems={[{ title: "Khoản thu khách hàng" }, { title: "Danh sách" }]}
        >
            {/* Mini Dashboard - 4 card */}
            <Row gutter={[16, 16]} className="mb-4">
                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        icon={<FileTextOutlined />}
                        tone="brand"
                        label="Tổng khoản thu"
                        value={dashboard.total_collections ?? 0}
                    />
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        icon={<UserOutlined />}
                        tone="info"
                        label="Tổng khách hàng"
                        value={dashboard.total_customers ?? 0}
                    />
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        icon={<ClockCircleOutlined />}
                        tone="danger"
                        label="Tổng tiền quá hạn"
                        value={`${Number(dashboard.total_overdue_amount ?? 0).toLocaleString("vi-VN")} ₫`}
                    />
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        icon={<WalletOutlined />}
                        tone="success"
                        label="Trung bình / khách"
                        value={`${Number(dashboard.avg_per_customer ?? 0).toLocaleString("vi-VN")} ₫`}
                    />
                </Col>
            </Row>

            <ListToolbar
                filterDefs={filterDefs}
                visibleFilters={visibleFilters}
                onChangeVisibleFilters={setVisibleFilters}
                columnDefs={columnDefs}
                visibleColumns={visibleColumns}
                onChangeVisibleColumns={setVisibleColumns}
                defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
                hasActiveFilter={hasActiveFilter}
                onClearFilter={handleClearFilters}
                onReload={() => {
                    fetchFees(pagination.current, pagination.pageSize, searchText, filters.status, filters.overdue_range, dateRange);
                    fetchDashboard(pagination.current, pagination.pageSize, searchText, filters.status, filters.overdue_range, dateRange);
                }}
            />

            <ListTableCard
                columns={columns}
                dataSource={customerFees}
                loading={loading}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                }}
                onChange={handleTableChange}
                emptyText="Không có dữ liệu"
            />
        </BrandedPageLayout>
    );
}
